import { Button, Select, Space, Tooltip, Typography } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import type { FieldValues, FieldPath } from 'react-hook-form';
import { Form } from 'antd';
import { useBrandOptions } from '../../hooks/useBrandOptions';
import { tokenManager } from '../../utils/tokenManager';
import { useViewScope } from '../../utils/viewScope';

export interface TargetBrandFieldProps<T extends FieldValues> {
  /** RHF 欄位路徑，值為品牌 code。 */
  name: FieldPath<T>;
  label?: React.ReactNode;
  required?: boolean;
  help?: React.ReactNode;
}

/**
 * 去品牌化 M4 — 寫入表單的顯式「目標品牌」欄位。
 *
 * 核心規則（品牌隔離紅線）：
 *   - 寫入絕不繼承「檢視範圍」，一律要求顯式指定 targetBrand。
 *   - 檢視=單品牌時提供「帶入目前檢視」按鈕（顯式、可見、可改），不做隱藏預填。
 *   - 檢視=all（聚合）時強制手選，沒有預設值。
 *
 * 值走 RHF；送出時由呼叫端把值放進 body.targetBrand + X-Target-Brand header。
 */
export function TargetBrandField<T extends FieldValues>({
  name,
  label = '目標品牌',
  required = true,
  help,
}: TargetBrandFieldProps<T>) {
  const { control, formState, setValue } = useFormContext<T>();
  const brandOptions = useBrandOptions();
  const { brandForRequest, isAllScope } = useViewScope();

  const allowed = tokenManager.getAllowedBrands();
  const options = brandOptions
    .filter((o) => allowed.includes(o.value))
    .map((o) => ({ label: `${o.label}（${o.value}）`, value: o.value }));

  const fieldError = (formState.errors as Record<string, { message?: string }>)[
    name as string
  ];
  const errorMsg = fieldError?.message;

  // 檢視=單品牌 → 給「帶入目前檢視」一鍵按鈕（顯式、可見、可改）。
  const canPrefillFromView = !isAllScope && brandForRequest != null;

  const defaultHelp = isAllScope
    ? '目前檢視為「全部品牌（彙總）」，請明確選擇這筆資料要寫入哪個品牌（不會沿用檢視範圍）。'
    : '寫入品牌需顯式指定，不沿用檢視範圍；可按右側按鈕帶入目前檢視的品牌，也可自行更改。';

  return (
    <Form.Item
      label={label}
      required={required}
      validateStatus={errorMsg ? 'error' : undefined}
      help={errorMsg || help || defaultHelp}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Space.Compact style={{ width: '100%' }}>
            <Select
              aria-label="目標品牌"
              data-testid="target-brand-select"
              placeholder="請選擇目標品牌"
              style={{ flex: 1 }}
              value={(field.value as string) || undefined}
              options={options}
              onChange={(v) => field.onChange(v)}
              onBlur={field.onBlur}
              aria-invalid={!!errorMsg}
              aria-required={required || undefined}
            />
            {canPrefillFromView ? (
              <Tooltip title="帶入右上角目前檢視的品牌（可再更改）">
                <Button
                  data-testid="target-brand-prefill-btn"
                  onClick={() =>
                    setValue(
                      name,
                      brandForRequest as unknown as T[typeof name],
                      { shouldDirty: true, shouldValidate: true },
                    )
                  }
                >
                  帶入目前檢視
                </Button>
              </Tooltip>
            ) : null}
          </Space.Compact>
        )}
      />
      {isAllScope ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          聚合檢視下不預填品牌，請手動選擇。
        </Typography.Text>
      ) : null}
    </Form.Item>
  );
}
